/**
 * Modul crypto client-side.
 *
 * Toate operațiile criptografice se fac ÎN BROWSER — serverul nu vede niciodată
 * parola master, cheia master derivată, sau cheia privată.
 *
 * Primitive folosite (libsodium):
 *   - KDF:        Argon2id (sodium.crypto_pwhash)
 *   - Simetric:   XSalsa20-Poly1305 (secretbox, nonce 24B)
 *   - Asimetric:  X25519 + XSalsa20-Poly1305 (crypto_box_seal pentru sharing anonim-sender)
 *
 * Vezi spec §3 pentru fluxurile complete.
 */

import sodium from 'libsodium-wrappers-sumo';

let _ready: Promise<void> | null = null;

export async function ensureSodium(): Promise<void> {
  if (!_ready) {
    _ready = sodium.ready;
  }
  await _ready;
}

// ============================================================================
// Encoding helpers
// ============================================================================

// Helperii de codare NU folosesc libsodium: sunt sincroni și trebuie să meargă
// înainte de `sodium.ready` — hidratarea sesiunii din sessionStorage rulează
// la boot, înainte de primul render (vezi stores/sessionPersist.ts).

const _B64_CHUNK = 0x8000; // sub limita de argumente a lui String.fromCharCode

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += _B64_CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + _B64_CHUNK));
  }
  return btoa(binary);
}

export function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function strToBytes(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

export function bytesToStr(b: Uint8Array): string {
  // fatal: un plaintext care nu e UTF-8 valid înseamnă date corupte, nu text.
  return new TextDecoder('utf-8', { fatal: true }).decode(b);
}

// ============================================================================
// KDF — derivare master_key din parola master
// ============================================================================

export const DEFAULT_KDF_PARAMS = {
  algorithm: 'argon2id',
  opslimit: 3,           // iterații
  memlimit: 64 * 1024 * 1024, // 64 MB
  version: 1,
} as const;

export async function deriveMasterKey(
  password: string,
  salt: Uint8Array,
  params: { opslimit: number; memlimit: number } = DEFAULT_KDF_PARAMS,
): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.crypto_pwhash(
    32, // output: 32 bytes — cheia de secretbox
    password,
    salt,
    params.opslimit,
    params.memlimit,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

/** Parametrii KDF așa cum vin de la server (`/auth/salt`, `/auth/kdf`). */
export interface ServerKdfParams {
  mem_kib: number;
  iters: number;
}

/**
 * Convertește parametrii KDF primiți de la server, refuzând orice e sub
 * valorile cu care se creează conturile. Serverul e untrusted: fără acest
 * prag, un server compromis ar putea cere `iters=1` cu memorie minimă și ar
 * primi un `auth_hash` ieftin de atacat offline.
 */
export function kdfParamsFromServer(params: ServerKdfParams): {
  opslimit: number;
  memlimit: number;
} {
  const memlimit = params.mem_kib * 1024;
  if (params.iters < DEFAULT_KDF_PARAMS.opslimit || memlimit < DEFAULT_KDF_PARAMS.memlimit) {
    throw new Error('Parametri KDF sub pragul minim — refuz derivarea cheii.');
  }
  return { opslimit: params.iters, memlimit };
}

export async function generateSalt(): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
}

/** Hash secundar pentru login: SHA256(master_key || "login-v1") */
export async function computeAuthHash(masterKey: Uint8Array): Promise<Uint8Array> {
  // Folosim Web Crypto API nativ în loc de libsodium — non-sumo nu expune
  // crypto_hash_sha256, iar SubtleCrypto e disponibilă peste tot.
  const suffix = strToBytes('login-v1');
  const input = new Uint8Array(masterKey.length + suffix.length);
  input.set(masterKey, 0);
  input.set(suffix, masterKey.length);
  const digest = await crypto.subtle.digest('SHA-256', input);
  return new Uint8Array(digest);
}

// ============================================================================
// Key pair — X25519 pentru fiecare user
// ============================================================================

export interface KeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

/** Reface perechea din cheia privată (cheia publică X25519 e derivabilă). */
export async function keyPairFromPrivateKey(privateKey: Uint8Array): Promise<KeyPair> {
  await ensureSodium();
  return { publicKey: sodium.crypto_scalarmult_base(privateKey), privateKey };
}

export async function generateKeyPair(): Promise<KeyPair> {
  await ensureSodium();
  const kp = sodium.crypto_box_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

// ============================================================================
// Simetric — XSalsa20-Poly1305 (secretbox)
// ============================================================================

export interface SealedBlob {
  ciphertext: Uint8Array;
  nonce: Uint8Array;
}

export async function encryptSym(plaintext: Uint8Array, key: Uint8Array): Promise<SealedBlob> {
  await ensureSodium();
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const ciphertext = sodium.crypto_secretbox_easy(plaintext, nonce, key);
  return { ciphertext, nonce };
}

export async function decryptSym(
  blob: SealedBlob,
  key: Uint8Array,
): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.crypto_secretbox_open_easy(blob.ciphertext, blob.nonce, key);
}

/**
 * Derivă o cheie de 32 bytes din `key` pentru un context dat (BLAKE2b keyed).
 * Contexte diferite dau chei independente — folosit ca să nu criptăm date
 * auxiliare (ex. drafturi locale) direct cu `master_key`.
 */
export async function deriveSubkey(key: Uint8Array, context: string): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.crypto_generichash(32, sodium.from_string(context), key);
}

/** Random 32-byte key for data encryption (DEK). */
export async function generateDEK(): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.randombytes_buf(sodium.crypto_secretbox_KEYBYTES);
}

// ============================================================================
// Criptare cu parolă — fișiere care părăsesc aplicația (export de vault)
// ============================================================================

export interface PasswordSealedBlob {
  ciphertext: Uint8Array;
  nonce: Uint8Array;
  salt: Uint8Array;
  opslimit: number;
  memlimit: number;
}

/**
 * Criptează `plaintext` cu o cheie derivată din parolă (Argon2id, salt nou la
 * fiecare apel). Cheia derivată nu părăsește funcția și e ștearsă din memorie
 * indiferent de rezultat. Saltul și parametrii KDF se întorc alături de blob —
 * nu sunt secrete și sunt necesare la decriptare.
 */
export async function encryptWithPassword(
  plaintext: Uint8Array,
  password: string,
  params: { opslimit: number; memlimit: number } = DEFAULT_KDF_PARAMS,
): Promise<PasswordSealedBlob> {
  const salt = await generateSalt();
  const key = await deriveMasterKey(password, salt, params);
  try {
    const blob = await encryptSym(plaintext, key);
    return { ...blob, salt, opslimit: params.opslimit, memlimit: params.memlimit };
  } finally {
    sodium.memzero(key);
  }
}

/**
 * Inversul lui `encryptWithPassword`. Aruncă dacă parola e greșită sau blob-ul
 * a fost modificat (secretbox e autentificat). Apelantul răspunde de limitele
 * parametrilor KDF când aceștia vin dintr-o sursă nesigură.
 */
export async function decryptWithPassword(
  blob: PasswordSealedBlob,
  password: string,
): Promise<Uint8Array> {
  const key = await deriveMasterKey(password, blob.salt, {
    opslimit: blob.opslimit,
    memlimit: blob.memlimit,
  });
  try {
    return await decryptSym(blob, key);
  } finally {
    sodium.memzero(key);
  }
}

// ============================================================================
// Asimetric — sealed_box pentru wrap/unwrap DEK (sharing)
// ============================================================================

/**
 * Împachetează DEK-ul cu cheia publică a destinatarului.
 * sealed_box = ECDH efemer + XSalsa20-Poly1305, sender anonim.
 */
export async function wrapKey(
  dek: Uint8Array,
  recipientPublicKey: Uint8Array,
): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.crypto_box_seal(dek, recipientPublicKey);
}

/** Despachetează un DEK wrapped cu cheia publică a userului curent. */
export async function unwrapKey(
  wrapped: Uint8Array,
  keyPair: KeyPair,
): Promise<Uint8Array> {
  await ensureSodium();
  return sodium.crypto_box_seal_open(wrapped, keyPair.publicKey, keyPair.privateKey);
}

// ============================================================================
// High-level — criptare/decriptare credential payload
// ============================================================================

export interface CredentialPayload {
  username?: string;
  password?: string;
  url?: string;
  notes?: string;
  [extra: string]: unknown;
}

export interface EncryptedCredential {
  ciphertext: string; // base64
  nonce: string;      // base64
  dek_wrapped: string; // base64 — pentru owner
}

export async function encryptCredential(
  payload: CredentialPayload,
  ownerPublicKey: Uint8Array,
): Promise<EncryptedCredential> {
  await ensureSodium();
  const dek = await generateDEK();
  // DEK-ul se șterge din memorie și dacă ceva aruncă (best-effort în JS).
  try {
    const plaintext = strToBytes(JSON.stringify(payload));
    const blob = await encryptSym(plaintext, dek);
    const wrapped = await wrapKey(dek, ownerPublicKey);
    return {
      ciphertext: bytesToBase64(blob.ciphertext),
      nonce: bytesToBase64(blob.nonce),
      dek_wrapped: bytesToBase64(wrapped),
    };
  } finally {
    sodium.memzero(dek);
  }
}

export interface EncryptedCredentialMulti {
  ciphertext: string;
  nonce: string;
  /** {user_id -> base64_wrapped_dek} pentru toți userii din `recipients` */
  dek_wraps: Record<string, string>;
  /** wrap pentru caller (primul user din recipients cu `isSelf=true`) — compat */
  dek_wrapped: string;
}

/**
 * Criptează payload-ul cu un DEK nou și wrap-uie DEK-ul pentru mai mulți useri
 * simultan (owner + shares). `recipients` conține {userId, publicKey, isSelf?}.
 * Întoarce dict-ul pentru a-l trimite direct în `CredentialUpdate.dek_wraps`.
 *
 * Forward secrecy: DEK nou la fiecare apel. User-ii revocați înainte de apel
 * nu primesc wrap → zero-acces after revoke (+ rotate).
 */
export async function encryptCredentialForShares(
  payload: CredentialPayload,
  recipients: Array<{ userId: string; publicKey: Uint8Array; isSelf?: boolean }>,
): Promise<EncryptedCredentialMulti> {
  if (recipients.length === 0) {
    throw new Error('encryptCredentialForShares: lista de recipients e goală');
  }
  await ensureSodium();
  const dek = await generateDEK();
  try {
    const plaintext = strToBytes(JSON.stringify(payload));
    const blob = await encryptSym(plaintext, dek);
    const dek_wraps: Record<string, string> = {};
    let selfWrap: string | null = null;
    for (const r of recipients) {
      const wrapped = await wrapKey(dek, r.publicKey);
      const b64 = bytesToBase64(wrapped);
      dek_wraps[r.userId] = b64;
      if (r.isSelf) selfWrap = b64;
    }
    if (selfWrap === null) {
      // Fallback: primul wrap din listă (util pentru rollback pe response)
      selfWrap = dek_wraps[recipients[0].userId];
    }
    return {
      ciphertext: bytesToBase64(blob.ciphertext),
      nonce: bytesToBase64(blob.nonce),
      dek_wraps,
      dek_wrapped: selfWrap,
    };
  } finally {
    sodium.memzero(dek);
  }
}

export async function decryptCredential(
  enc: { ciphertext: string; nonce: string; dek_wrapped: string },
  keyPair: KeyPair,
): Promise<CredentialPayload> {
  await ensureSodium();
  const plaintext = await decryptWithWrappedKey(enc, keyPair);
  return JSON.parse(bytesToStr(plaintext)) as CredentialPayload;
}

/**
 * Desface DEK-ul cu cheia privată, decriptează blob-ul și șterge DEK-ul din
 * memorie indiferent de rezultat. Toate citirile de conținut (notițe,
 * versiuni, atașamente, credentiale) trec pe aici, ca niciun apelant să nu
 * uite DEK-ul în memorie.
 */
export async function decryptWithWrappedKey(
  enc: { ciphertext: string; nonce: string; dek_wrapped: string },
  keyPair: KeyPair,
): Promise<Uint8Array> {
  await ensureSodium();
  const dek = await unwrapKey(base64ToBytes(enc.dek_wrapped), keyPair);
  try {
    return await decryptSym(
      { ciphertext: base64ToBytes(enc.ciphertext), nonce: base64ToBytes(enc.nonce) },
      dek,
    );
  } finally {
    sodium.memzero(dek);
  }
}
